import type { SxProps, Theme } from "@mui/material";
import { keyframes } from "@mui/material/styles";

const COLORS = {
  white: "#FFFFFF",
  error: "#ED3D5A",
  aiPurple: "#76118B",
  aiPurplePillBg: "#EDD8F2",
  aiPurpleTintBg: "rgba(187, 21, 220, 0.08)",
  aiButtonBg: "rgba(118, 17, 139, 0.1)",
  aiButtonBorder: "rgba(118, 17, 139, 0.15)",
  aiButtonHoverBg: "rgba(118, 17, 139, 0.18)",
  gradientViolet: "#6A2DE6",
  gradientMagenta: "#E70A74",
  gradientBlue: "#1D9DEE",
};

const gradientBorderTraverse = keyframes`
  100% { transform: rotate(360deg); }
`;

const buttonShimmer = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

const refreshIconSpin = keyframes`
  100% { transform: rotate(360deg); }
`;

export const GRADIENT_BORDER = `linear-gradient(95.69deg, ${COLORS.gradientViolet} 0%, ${COLORS.gradientViolet} 38.26%, ${COLORS.gradientMagenta} 71.61%)`;
export const GRADIENT_BUTTON = `linear-gradient(90deg, ${COLORS.gradientBlue} 0%, ${COLORS.gradientViolet} 50%, ${COLORS.gradientMagenta} 100%)`;

export const formControlSx: SxProps<Theme> = {
  marginTop: 0,
  "& .MuiOutlinedInput-root": { alignItems: "flex-start" },
};

const LOADING_BORDER_BASE = `conic-gradient(from 0deg, rgba(106, 45, 230, 0.4) 0deg 270deg, ${COLORS.gradientViolet} 270deg 360deg)`;
const LOADING_BORDER_HEAD = `conic-gradient(from 0deg, transparent 0deg 250deg, ${COLORS.gradientMagenta} 250deg 280deg, ${COLORS.gradientBlue} 280deg 310deg, rgba(106, 45, 230, 0.5) 310deg 360deg)`;

export function getBorderWrapperSx(
  error: boolean,
  loading?: boolean
): SxProps<Theme> {
  if (error) {
    return {
      border: `2px solid ${COLORS.error}`,
      borderRadius: "10px",
      padding: 0,
      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    };
  }
  if (loading) {
    return {
      position: "relative",
      overflow: "hidden",
      borderRadius: "10px",
      padding: 0,
      "&::before": {
        content: '""',
        position: "absolute",
        inset: "-2px",
        background: LOADING_BORDER_BASE,
        borderRadius: "10px",
      },
      "&::after": {
        content: '""',
        position: "absolute",
        inset: "-2px",
        background: LOADING_BORDER_HEAD,
        borderRadius: "10px",
        animation: `${gradientBorderTraverse} 1.8s linear infinite`,
      },
      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    };
  }
  return {
    padding: "2px",
    borderRadius: "10px",
    background: GRADIENT_BORDER,
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  };
}

export const innerStackSx: SxProps<Theme> = {
  borderRadius: "8px",
  bgcolor: COLORS.white,
  overflow: "hidden",
};

export const writeWithAiButtonSx: SxProps<Theme> = {
  background: GRADIENT_BUTTON,
  color: COLORS.white,
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "9999px",
  px: 2,
  "&:hover": {
    background: GRADIENT_BUTTON,
    opacity: 0.92,
  },
  "&.Mui-disabled": {
    color: COLORS.white,
    opacity: 0.7,
  },
};

const BUTTON_LOADING_GRADIENT = `linear-gradient(90deg, ${COLORS.gradientBlue} 0%, ${COLORS.gradientViolet} 25%, ${COLORS.gradientMagenta} 50%, ${COLORS.gradientViolet} 75%, ${COLORS.gradientBlue} 100%)`;

export function getWriteWithAiButtonSx(loading: boolean): SxProps<Theme> {
  if (!loading) return writeWithAiButtonSx;
  return {
    ...writeWithAiButtonSx,
    background: BUTTON_LOADING_GRADIENT,
    backgroundSize: "200% 100%",
    animation: `${buttonShimmer} 1.5s ease-in-out infinite`,
    pointerEvents: "none",
    "&:hover": {
      background: BUTTON_LOADING_GRADIENT,
      backgroundSize: "200% 100%",
      opacity: 1,
    },
  };
}

export const aiBlockSx: SxProps<Theme> = {
  m: 1,
  padding: "12px",
  bgcolor: COLORS.aiPurpleTintBg,
  borderRadius: "16px",
};

export const aiPillSx: SxProps<Theme> = {
  alignItems: "center",
  gap: 0.625,
  padding: "4px 7px",
  bgcolor: COLORS.aiPurplePillBg,
  borderRadius: "10px",
};

export const aiLabelSx: SxProps<Theme> = {
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "22px",
  letterSpacing: "-0.02em",
  color: COLORS.aiPurple,
};

export const aiBodySx: SxProps<Theme> = {
  ...aiLabelSx,
  whiteSpace: "pre-wrap",
};

export const aiIconButtonSx: SxProps<Theme> = {
  width: 28,
  height: 28,
  minWidth: 28,
  minHeight: 28,
  padding: 0,
  backgroundColor: COLORS.aiButtonBg,
  border: `1px solid ${COLORS.aiButtonBorder}`,
  borderRadius: 1,
  color: COLORS.aiPurple,
  "&:hover": { backgroundColor: COLORS.aiButtonHoverBg },
};

export const aiIconSx: SxProps<Theme> = {
  width: 20,
  height: 20,
  color: COLORS.aiPurple,
};

export function getRefreshIconLoadingSx(loading: boolean): SxProps<Theme> {
  if (!loading) return { color: COLORS.white };
  return {
    color: COLORS.white,
    animation: `${refreshIconSpin} 1s linear infinite`,
  };
}

export { COLORS };
